import React, { useState } from "react";
import { Eye, Edit, Trash, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Chip, Modal, Box, CircularProgress } from "@mui/material";
import { Permissions } from "../../../../permissions";

/**
 * RoleCard component for displaying role information
 * @param {Object} props Component props
 * @param {Object} props.role Role data
 * @param {Function} props.onDelete Function to call when delete is confirmed
 */
const RoleCard = ({ role, onDelete }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check user permissions
  const canEditRole = Permissions.includes("edit_role");
  const canDeleteRole = Permissions.includes("delete_role");

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (onDelete) {
      setIsDeleting(true);
      try {
        await onDelete(role.id);
      } finally {
        setIsDeleting(false);
        setDeleteModalOpen(false);
      }
    }
  };

  // Navigate to edit role page
  const handleEditRole = () => {
    navigate(`/usermanagement/editrole`, { state: { roleId: role.id } });
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm overflow-hidden">
      <div className="p-5">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-[#374151] font-outfit">
              {role.name}
            </h3>
            <p className="text-sm text-[#6B7280] mt-1 font-outfit">
              {role.description}
            </p>
          </div>
          <div className="flex space-x-2">
            {canEditRole ? (
              <button
                onClick={handleEditRole}
                className="p-2 bg-[rgb(86,82,176,0.1)] text-[#5652B0] rounded-md hover:bg-[rgb(86,82,176,0.2)] transition-all"
                title="Edit role">
                <Edit size={16} />
              </button>
            ) : (
              <button
                disabled
                className="p-2 bg-gray-100 text-gray-400 rounded-md cursor-not-allowed"
                title="You don't have permission to edit roles">
                <Edit size={16} />
              </button>
            )}

            {canDeleteRole ? (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="p-2 bg-[rgba(220,38,38,0.1)] text-red-600 rounded-md hover:bg-[rgba(220,38,38,0.2)] transition-all"
                title="Delete role">
                <Trash size={16} />
              </button>
            ) : (
              <button
                disabled
                className="p-2 bg-gray-100 text-gray-400 rounded-md cursor-not-allowed"
                title="You don't have permission to delete roles">
                <Trash size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500 font-outfit">
              Permission groups:{" "}
              <span className="font-medium">
                {role.permissionGroups.length}
              </span>
            </div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center text-[#5652B0] hover:text-[#4A47A3] text-sm font-medium">
              {expanded ? (
                <>
                  <span className="mr-1">Hide details</span>
                  <ChevronUp size={16} />
                </>
              ) : (
                <>
                  <span className="mr-1">View details</span>
                  <ChevronDown size={16} />
                </>
              )}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {role.permissionGroups
              .slice(0, expanded ? role.permissionGroups.length : 3)
              .map((group, index) => (
                <Chip
                  key={`${role.id}-${index}`}
                  label={group}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(86, 82, 176, 0.1)",
                    color: "#5652B0",
                    fontFamily: "Outfit",
                    fontSize: "12px",
                    fontWeight: 500,
                    "& .MuiChip-label": {
                      padding: "0 10px",
                    },
                  }}
                />
              ))}
            {!expanded && role.permissionGroups.length > 3 && (
              <Chip
                label={`+${role.permissionGroups.length - 3} more`}
                size="small"
                sx={{
                  backgroundColor: "rgba(107, 114, 128, 0.1)",
                  color: "#6B7280",
                  fontFamily: "Outfit",
                  fontSize: "12px",
                  fontWeight: 500,
                  "& .MuiChip-label": {
                    padding: "0 10px",
                  },
                }}
              />
            )}
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
            <div className="mb-3">
              <h4 className="text-sm font-medium text-[#374151] mb-2 font-outfit">
                Permission Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {role.permissionGroups.map((group, index) => (
                  <div
                    key={`detail-${role.id}-${index}`}
                    className="text-sm text-[#6B7280] flex items-center">
                    <div className="w-2 h-2 rounded-full bg-[#5652B0] mr-2"></div>
                    {group}
                  </div>
                ))}
              </div>
            </div>

            <div className="text-xs text-gray-500 flex justify-between mt-4 pt-2 border-t border-gray-100">
              <div>
                Created: {new Date(role.createdAt).toLocaleDateString()}
              </div>
              <div>
                Last updated: {new Date(role.updatedAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => !isDeleting && setDeleteModalOpen(false)}
        aria-labelledby="delete-role-modal">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}>
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Trash size={24} className="text-red-600" />
            </div>

            <h3 className="text-lg font-medium text-gray-900 mb-2 font-outfit">
              Delete Role
            </h3>
            <p className="text-sm text-gray-500 mb-6 font-outfit">
              Are you sure you want to delete the{" "}
              <span className="font-semibold">{role.name}</span> role? This
              action cannot be undone.
            </p>

            <div className="flex justify-center space-x-3">
              <button
                onClick={() => !isDeleting && setDeleteModalOpen(false)}
                className="px-4 py-2 border border-[#5652B0] text-[#5652B0] rounded-lg hover:bg-[rgb(86,82,176,0.05)] transition-all disabled:opacity-50"
                disabled={isDeleting}>
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all flex justify-center items-center disabled:opacity-50"
                disabled={isDeleting}>
                {isDeleting ? (
                  <>
                    <CircularProgress
                      size={16}
                      color="inherit"
                      className="mr-2"
                    />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default RoleCard;
